import React, { useEffect, useMemo, useState } from 'react';
import BigNumber from 'bignumber.js';
import { ChainId, Token, Tokens, NETWORK_CONFIG } from '../../../../constants';
import { BridgeStrategy, BridgeConfig } from '../../types';
import BridgeToken from '../../BridgeToken';
import useBridge from '@/hooks/useBridge';
import useTokenApprove from '@/hooks/use-token-approve';
import useTokenBalance from '@/hooks/use-token-balance';
import { isEthereum, isMetisChain } from '@/utils';
import AlertFunc from '@/utils/components/alert';
import ModalFunc from '@/utils/components/modal';
import Row from '@/components/Row';
import Alert from '@/components/Alert';
import user from '@/stores/user';
import { observer } from 'mobx-react';

interface StandardBridgeProps {
    fromChainId: ChainId;
    toChainId: ChainId;
    currentToken: Token;
    setCurrentToken: (token: Token) => void;
    onTransactionSuccess: (
        tx: string,
        amount: BigNumber,
        tokenName: string,
    ) => void;
}

const CONFIRM_BLOCKNUMBERS = 12;

function StandardBridge({
    fromChainId,
    toChainId,
    currentToken,
    setCurrentToken,
    onTransactionSuccess,
}: StandardBridgeProps) {
    const { connected, address: walletAddress } = user;

    const [inputAmount, setInputAmount] = useState(new BigNumber(0));
    const [transferring, setTransferring] = useState(false);
    const [transFeeInfo, setTransFeeInfo] = useState<{
        fee_num: number;
        servicesFee: number;
    }>({
        fee_num: 0,
        servicesFee: 0,
    });
    const [loadingTransFee, setLoadingTransFee] = useState(false);
    const [inputErrorMessage, setInputErrorMessage] = useState<
        string | undefined
    >(undefined);

    const [confirmBlockNumber, setConfirmBlockNumber] = useState<number>(0);
    const [transAlertShow, setTransAlertShow] = useState<boolean>(false);

    const { approved, approveUserTokenForSpender } = useTokenApprove(
        currentToken,
        inputAmount,
        fromChainId,
        toChainId,
    );

    const {
        bridgeToken,
        getBlockNumberFromHash,
        getCurrnetBlockNumber,
        getBridgeTokenParams,
        estimateBridgeTokenGas,
    } = useBridge();

    const {
        balance: currentTokenBalance,
        getBalance: updateCurrentTokenBalance,
    } = useTokenBalance(
        (Tokens[currentToken] as any)[fromChainId]?.address || '',
        undefined,
        {
            intervalTime: 10000,
            isOft: !!(Tokens[currentToken] as any)[fromChainId]?.oft,
        },
    );

    const isDeposit = useMemo(() => {
        return isEthereum(fromChainId);
    }, [fromChainId]);

    // Bridge configuration based on chain pair
    const bridgeConfig: BridgeConfig = useMemo(() => {
        if (isDeposit) {
            // Ethereum/Sepolia -> Metis
            return {
                etaText: `From ${NETWORK_CONFIG[fromChainId].name} to ${NETWORK_CONFIG[toChainId].name}: less than 10 minutes`,
                etaTooltip: `From ${NETWORK_CONFIG[fromChainId].name} to ${NETWORK_CONFIG[toChainId].name}: less than 10 minutes`,
                timeDisplay: 'Less than 10 minutes',
                showProtocolFee: false,
                showDate: false,
                gasTokenName: 'ETH',
            };
        } else {
            // Metis -> Ethereum/Sepolia
            return {
                etaText: `Withdrawals from ${NETWORK_CONFIG[fromChainId].name} to ${NETWORK_CONFIG[toChainId].name} require a wait time of one week.`,
                etaTooltip: `Withdrawals from ${NETWORK_CONFIG[fromChainId].name} to ${NETWORK_CONFIG[toChainId].name} require a wait time of one week.`,
                timeDisplay: '7 DAYS',
                showProtocolFee: true,
                showDate: true,
                gasTokenName: 'METIS',
            };
        }
    }, [fromChainId, toChainId, isDeposit]);

    const { balance: gasTokenBalance } = useTokenBalance('');

    // Input validation for Standard Bridge
    useEffect(() => {
        if (inputAmount.isGreaterThan(currentTokenBalance)) {
            setInputErrorMessage('Insufficient Balance');
        } else if (inputAmount.isGreaterThan(0) && isMetisChain(fromChainId)) {
            // For Metis withdrawal, check if enough gas is reserved
            getLeftAmount().then((leftAmount) => {
                if (leftAmount) {
                    if (
                        (currentToken !== Token.metis &&
                            gasTokenBalance.isLessThan(leftAmount)) ||
                        (currentToken === Token.metis &&
                            gasTokenBalance
                                .minus(inputAmount)
                                .isLessThan(leftAmount))
                    ) {
                        setInputErrorMessage(
                            `You should reserve at least ${
                                leftAmount?.toFixed() || '-'
                            } METIS as the bridging cost to ${
                                NETWORK_CONFIG[toChainId].name
                            }.`,
                        );
                    } else {
                        setInputErrorMessage(undefined);
                    }
                }
            });
        } else {
            setInputErrorMessage(undefined);
        }
    }, [
        inputAmount,
        currentTokenBalance,
        gasTokenBalance,
        fromChainId,
        currentToken,
    ]);

    useEffect(() => {
        setInputAmount(new BigNumber(0));
    }, [fromChainId]);

    // Validation logic for standard bridge
    function validateInput(
        amount: BigNumber,
        balance: BigNumber,
        gasBalance: BigNumber,
        leftAmount?: BigNumber,
    ): string | undefined {
        if (amount.isGreaterThan(balance)) {
            return 'Insufficient Balance';
        }

        // For withdraw (Metis -> Ethereum), check if enough METIS for gas
        if (!isDeposit && amount.isGreaterThan(0) && leftAmount) {
            if (
                (currentToken !== Token.metis &&
                    gasBalance.isLessThan(leftAmount)) ||
                (currentToken === Token.metis &&
                    gasBalance.minus(amount).isLessThan(leftAmount))
            ) {
                return `You should reserve at least ${
                    leftAmount?.toFixed() || '-'
                } METIS as the bridging cost to Ethereum.`;
            }
        }

        return undefined;
    }

    async function transfer(recipientAddress: string): Promise<string | false> {
        setTransferring(true);

        let tx: any = false;

        try {
            tx = await bridgeToken(
                fromChainId,
                toChainId,
                recipientAddress,
                inputAmount,
                currentToken,
            );

            if (tx) {
                if (isDeposit) {
                    setTransAlertShow(true);

                    const transferAmount = new BigNumber(inputAmount);

                    const interval = setInterval(async () => {
                        const txNumber = await getBlockNumberFromHash(
                            fromChainId,
                            tx,
                        );

                        const currentBlockNumber = await getCurrnetBlockNumber(
                            fromChainId,
                        );
                        if (
                            currentBlockNumber &&
                            txNumber &&
                            currentBlockNumber > txNumber
                        ) {
                            const confirm = currentBlockNumber - txNumber;
                            if (confirm < CONFIRM_BLOCKNUMBERS) {
                                setConfirmBlockNumber(confirm);
                            } else {
                                setTransAlertShow(false);
                                setConfirmBlockNumber(0);
                                clearInterval(interval);
                                onTransactionSuccess(
                                    tx,
                                    transferAmount,
                                    (Tokens[currentToken] as any)[fromChainId]
                                        ?.symbol || '',
                                );
                            }
                        }
                    }, 3000);
                } else {
                    onTransactionSuccess(
                        tx,
                        inputAmount,
                        (Tokens[currentToken] as any)[fromChainId]?.symbol ||
                            '',
                    );
                }

                setInputAmount(new BigNumber(0));
            }
        } catch (error) {
            console.error('Standard bridge transfer error:', error);
        }

        setTransferring(false);
        return tx;
    }

    async function getTransFeeDetail(
        recipientAddress: string,
        amount: BigNumber,
    ) {
        if (!approved || transferring) return;

        if (
            amount.isGreaterThan(0) &&
            currentTokenBalance.isGreaterThanOrEqualTo(amount)
        ) {
            try {
                setLoadingTransFee(true);

                const params: any = await getBridgeTokenParams(
                    fromChainId,
                    toChainId,
                    recipientAddress,
                    amount,
                    currentToken,
                );
                if (params) {
                    const estimateGas = await estimateBridgeTokenGas(
                        fromChainId,
                        toChainId,
                        amount,
                        currentToken,
                        params,
                    );
                    setTransFeeInfo({
                        fee_num:
                            estimateGas && estimateGas.fee_num
                                ? estimateGas.fee_num
                                : 0,
                        servicesFee:
                            estimateGas && estimateGas.servicesFee
                                ? estimateGas.servicesFee
                                : 0,
                    });
                }
            } catch (e) {
                console.error('getTransFeeDetail error');
                console.error(e);
            }
            setLoadingTransFee(false);
        }
    }

    async function getLeftAmount(): Promise<BigNumber | undefined> {
        if (!currentTokenBalance) return undefined;

        const params = await getBridgeTokenParams(
            fromChainId,
            toChainId,
            walletAddress || '',
            currentTokenBalance,
            currentToken,
        );
        if (params) {
            const estimateGas = await estimateBridgeTokenGas(
                fromChainId,
                toChainId,
                currentTokenBalance,
                currentToken,
                params,
            );

            return estimateGas?.estimateRemainingMainCoin;
        }

        return undefined;
    }

    async function handleMax(recipientAddress: string) {
        if (!currentTokenBalance) return;

        if (
            currentTokenBalance.isGreaterThan(0) &&
            ((isEthereum(fromChainId) && currentToken === Token.eth) ||
                (!isEthereum(fromChainId) && currentToken === Token.metis))
        ) {
            try {
                const params = await getBridgeTokenParams(
                    fromChainId,
                    toChainId,
                    recipientAddress,
                    currentTokenBalance,
                    currentToken,
                );

                if (params) {
                    const estimateGas = await estimateBridgeTokenGas(
                        fromChainId,
                        toChainId,
                        currentTokenBalance,
                        currentToken,
                        params,
                    );

                    if (estimateGas && estimateGas.estimateRemainingMainCoin) {
                        const leftAmount = currentTokenBalance
                            .minus(estimateGas.estimateRemainingMainCoin)
                            .minus(isEthereum(fromChainId) ? 0.005 : 0.1)
                            .minus(estimateGas.fee_num || 0);

                        if (
                            estimateGas.estimateRemainingMainCoin.isGreaterThan(
                                currentTokenBalance,
                            ) ||
                            leftAmount.isLessThanOrEqualTo(0)
                        ) {
                            ModalFunc({
                                type: 'sorry',
                                title: 'Sorry',
                                text: `The current ${
                                    Tokens[currentToken][fromChainId]?.symbol ||
                                    ''
                                } Token balance ${currentTokenBalance.toFixed(
                                    3,
                                )} on your account is not sufficient to support this execution. Please leave at least ${estimateGas.estimateRemainingMainCoin
                                    .plus(estimateGas.fee_num)
                                    .toFixed(6)} ${
                                    Tokens[currentToken][fromChainId]?.symbol ||
                                    ''
                                } to support this execution`,
                                buttonText: 'OK',
                            });
                        } else {
                            setInputAmount(leftAmount);

                            AlertFunc({
                                type: 'info',
                                text: `Your estimated gas fee has been subtracted from your balance.`,
                                duration: 10000,
                            });
                        }
                    }
                }
            } catch (e) {
                console.error(e);
            }
        } else {
            setInputAmount(currentTokenBalance);
        }
    }

    const bridgeStrategy: BridgeStrategy = {
        currentToken,
        setCurrentToken,
        currentTokenBalance,
        updateTokenBalance: updateCurrentTokenBalance,
        inputAmount,
        setInputAmount,
        inputErrorMessage,
        approved,
        approveToken: approveUserTokenForSpender,
        transfer,
        transferring,
        estimateFee: getTransFeeDetail,
        transFeeInfo,
        loadingTransFee,
        handleMax,
        getLeftAmount,
        fromChainId,
        toChainId,
        bridgeConfig,
    };

    return (
        <>
            <BridgeToken
                strategy={bridgeStrategy}
                connected={connected}
                walletAddress={walletAddress}
                onConnect={() => user.toggleConnectModal()}
            />
            <Alert
                isOpen={transAlertShow}
                duration={1000000}
                type="loading"
                onClose={() => {
                    setTransAlertShow(false);
                }}
                anchorOrigin={{
                    horizontal: 'right',
                    vertical: 'top',
                }}
                text={
                    <>
                        <Row lineHeight="none">Pending</Row>
                        <Row lineHeight="none">
                            Blocks Confirms({confirmBlockNumber}/
                            {CONFIRM_BLOCKNUMBERS})
                        </Row>
                    </>
                }
            />
        </>
    );
}

export default observer(StandardBridge);
